/**
 * Lead Enrichment panel for the Lead Command Center.
 *
 * Shows what backend/lead_enrichment.py found for a lead: status, last run,
 * failure reason, which fields it filled (and from where), detected booking
 * platform / online booking, website title + description, services,
 * socials and contacts found. Read-only; "Enrich now" lives in the Command
 * Center header and passes the refreshed lead back via onLeadUpdated.
 */
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Zap, RefreshCw, CheckCircle2, Clock, XCircle, AlertCircle, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  enriched: { label: 'Enriched', icon: CheckCircle2, className: 'bg-green-500/20 text-green-300 border-green-500/30' },
  completed: { label: 'Enriched', icon: CheckCircle2, className: 'bg-green-500/20 text-green-300 border-green-500/30' },
  queued: { label: 'Queued', icon: Clock, className: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  processing: { label: 'Processing', icon: RefreshCw, className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  failed: { label: 'Failed', icon: XCircle, className: 'bg-red-500/20 text-red-300 border-red-500/30' },
  skipped: { label: 'Skipped', icon: AlertCircle, className: 'bg-white/10 text-white/60 border-white/20' },
};

const REASON_TEXT = {
  no_website: 'No website on file',
  timeout: 'Website timed out',
  connection_error: 'Could not connect to the website',
  dns_failure: 'Website domain does not resolve',
  not_html: 'Website did not return an HTML page',
  too_many_redirects: 'Website redirected too many times',
  private_address: 'Website address is not publicly reachable (blocked)',
  internal_hostname: 'Website address is not a public domain (blocked)',
  invalid_hostname: 'Website address is invalid',
  invalid_url: 'Website address is invalid',
  scheme_not_allowed: 'Only http/https websites can be enriched',
  port_not_allowed: 'Website uses a non-standard port (blocked)',
  credentials_in_url: 'Website address contains credentials (blocked)',
  stuck_in_processing: 'Enrichment repeatedly stalled',
};

const FIELD_LABELS = {
  email: 'Email',
  phone: 'Phone',
  instagram_url: 'Instagram',
  facebook_url: 'Facebook',
  tiktok_url: 'TikTok',
  linkedin_url: 'LinkedIn',
  google_business_url: 'Google profile',
  services_detected: 'Services',
  booking_system: 'Booking system',
  has_online_booking: 'Online booking',
  rating: 'Google rating',
  review_count: 'Review count',
  reviews_count: 'Review count (legacy)',
  business_hours: 'Business hours',
  place_id: 'Google place id',
};

// Only ever render http(s) links - these URLs come from scraped websites.
const safeHref = (url) => (typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null);

const formatWhen = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

const describeReason = (reason) => {
  if (!reason) return null;
  if (REASON_TEXT[reason]) return REASON_TEXT[reason];
  if (/^http_\d+$/.test(reason)) return `Website returned HTTP ${reason.slice(5)}`;
  return reason.replace(/_/g, ' ');
};

const LeadEnrichmentPanel = ({ lead, user, onLeadUpdated }) => {
  const [refreshing, setRefreshing] = useState(false);
  const status = lead?.enrichment_status;
  if (!lead || !status || status === 'not_started') return null;

  const style = STATUS_STYLES[status] || STATUS_STYLES.skipped;
  const StatusIcon = style.icon;
  const data = lead.enrichment_data || {};
  const website = data.website || {};
  const sources = data.sources || {};
  const socials = data.socials || {};
  const filled = Object.keys(sources).filter((field) => FIELD_LABELS[field]);
  const lastRun = formatWhen(lead.enrichment_last_run_at || lead.enrichment_timestamp);
  const nextAttempt = status === 'queued' ? formatWhen(lead.enrichment_next_attempt_at) : null;
  const reason = ['failed', 'skipped', 'queued'].includes(status) ? describeReason(lead.enrichment_error) : null;

  const refresh = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API}/phase9/leads/${lead.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (response.ok) {
        onLeadUpdated(await response.json());
      } else {
        toast.error('Could not refresh lead');
      }
    } catch (error) {
      toast.error('Could not refresh lead');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <section className="glass-panel" data-testid="lead-enrichment-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Zap className="h-4 w-4 text-blue-300" />
          <span className="text-sm font-semibold text-white/90">Enrichment</span>
          <Badge className={`${style.className} flex items-center gap-1`} data-testid="lead-enrichment-status">
            <StatusIcon size={12} className={status === 'processing' ? 'animate-spin' : ''} />
            {style.label}
          </Badge>
          {data.providers?.length > 0 && (
            <span className="text-xs text-white/50">via {data.providers.join(' + ')}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {lastRun && <span className="text-xs text-white/50">Last run {lastRun}</span>}
          <Button className="ghost-btn" onClick={refresh} disabled={refreshing} data-testid="lead-enrichment-refresh">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>
      </div>

      {status === 'queued' && (
        <p className="text-xs text-blue-300 mb-2">
          Waiting for the next enrichment run{nextAttempt ? ` (retry after ${nextAttempt})` : ''}.
        </p>
      )}
      {reason && (
        <p className={`text-xs mb-2 ${status === 'failed' ? 'text-red-300' : 'text-white/60'}`} data-testid="lead-enrichment-reason">
          {status === 'queued' ? 'Last attempt: ' : ''}{reason}
        </p>
      )}

      {(website.title || website.meta_description) && (
        <div className="mb-3">
          {website.title && <p className="text-sm text-white/90">{website.title}</p>}
          {website.meta_description && <p className="text-xs text-white/60">{website.meta_description}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        {(data.booking_platforms || data.has_online_booking !== undefined) && (
          <div>
            <p className="text-white/50 mb-1">Booking</p>
            <div className="flex flex-wrap gap-1">
              {(data.booking_platforms || []).map((name) => (
                <Badge key={name} className="bg-[var(--gold)]/20 text-[var(--gold)]">{name}</Badge>
              ))}
              <Badge className="bg-white/10 text-white/70">
                {data.has_online_booking ? 'Online booking found' : 'No online booking found'}
              </Badge>
              {website.platform && <Badge className="bg-white/10 text-white/70">Site: {website.platform}</Badge>}
            </div>
          </div>
        )}

        {data.services?.length > 0 && (
          <div>
            <p className="text-white/50 mb-1">Services mentioned</p>
            <div className="flex flex-wrap gap-1">
              {data.services.map((service) => (
                <Badge key={service} className="bg-white/10 text-white/70">{service}</Badge>
              ))}
            </div>
          </div>
        )}

        {Object.keys(socials).length > 0 && (
          <div>
            <p className="text-white/50 mb-1">Profiles found</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(socials).map(([network, url]) => {
                const href = safeHref(url);
                return href ? (
                  <a key={network} href={href} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-300 hover:underline inline-flex items-center gap-1">
                    {network.replace('_', ' ')} <ExternalLink size={10} />
                  </a>
                ) : null;
              })}
            </div>
          </div>
        )}

        {(data.emails?.length > 0 || data.phones?.length > 0) && (
          <div>
            <p className="text-white/50 mb-1">Contacts found</p>
            <p className="text-white/80 break-all">{[...(data.emails || []), ...(data.phones || [])].join(' · ')}</p>
          </div>
        )}

        {data.google && (data.google.rating || data.google.review_count) && (
          <div>
            <p className="text-white/50 mb-1">Google</p>
            <p className="text-white/80">
              {data.google.rating ? `${data.google.rating}★` : ''} {data.google.review_count ? `(${data.google.review_count} reviews)` : ''}
            </p>
          </div>
        )}
      </div>

      {filled.length > 0 && (
        <div className="mt-3 text-xs" data-testid="lead-enrichment-filled">
          <p className="text-white/50 mb-1">Filled by enrichment (blank fields only)</p>
          <div className="flex flex-wrap gap-1">
            {filled.map((field) => (
              <Badge key={field} className="bg-green-500/10 text-green-300 border-green-500/20" title={`${sources[field].source}${sources[field].filled_at ? ` · ${formatWhen(sources[field].filled_at)}` : ''}`}>
                {FIELD_LABELS[field]} · {sources[field].source}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default LeadEnrichmentPanel;
